// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import * as CosmeticCommands from '@app/features/cosmetics/commands/CosmeticCommands';
import {
	EMPTY_ROOM_OUTFIT,
	isRoomPartType,
	isWearableRoomPart,
	type RoomOutfit,
	roomSlotOf,
	wearRoomPart,
} from '@app/features/cosmetics/utils/RoomOutfitModels';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import Users from '@app/features/user/state/Users';
import {ROOM_OUTFIT_SLOTS, type RoomOutfitItemType, type RoomOutfitSlot} from '@fluxer/constants/src/RoomOutfit';
import type {
	CosmeticItemResponse,
	RoomOutfitResponse,
} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

export interface CosmeticItem extends CosmeticItemResponse {
	owned: boolean;
}

// The shared schema's CosmeticItemType widens to string, so the four slots are spelled out here.
export const COSMETIC_TYPES = ['avatar_decoration', 'avatar_ultra', 'nameplate', 'profile_effect'] as const;
export type CosmeticType = (typeof COSMETIC_TYPES)[number];
// Worn in 3D voice rooms only, with its own endpoint, so it is not one of the profile slots above.
export const ROOM_CHARACTER_TYPE = 'room_character';
// Room character parts (room_<slot>) save together through the outfit endpoint.
export type WearableType = CosmeticType | typeof ROOM_CHARACTER_TYPE | RoomOutfitItemType;
// Every type this client sells: the wearable ones and the 3D room body emotes, which are owned, never worn.
export type KnownCosmeticType = WearableType | 'room_emote';
export type CosmeticSelection = Partial<Record<WearableType, string | null>>;

const EQUIP_FIELDS = {
	avatar_decoration: 'avatar_decoration_id',
	avatar_ultra: 'avatar_ultra_id',
	profile_effect: 'profile_effect_id',
	nameplate: 'nameplate_id',
} as const satisfies Record<CosmeticType, keyof CosmeticCommands.EquipCosmeticsBody>;

function readOutfit(fields: RoomOutfitResponse): RoomOutfit {
	return Object.fromEntries(ROOM_OUTFIT_SLOTS.map((slot) => [slot, fields[`room_${slot}_id`] ?? null])) as RoomOutfit;
}

// What the character editor saves: a whole character, or the worn parts; both with the shirt colour.
export interface RoomLook {
	characterId: string | null;
	outfit: RoomOutfit;
	shirt: number | null;
}

export function changedSlots(next: RoomOutfit, worn: RoomOutfit): Partial<RoomOutfit> {
	return Object.fromEntries(
		ROOM_OUTFIT_SLOTS.filter((slot) => next[slot] !== worn[slot]).map((slot) => [slot, next[slot]]),
	);
}

// Owned items the catalog stopped offering stay wearable, so they follow the catalog. Items only an owner can wear
// stay hidden until they are owned: nothing on the profile tab can sell them. Room character parts stay, since the
// character editor shows them locked and opens the shop.
export function mergeCosmeticItems(
	catalog: ReadonlyArray<CosmeticItemResponse>,
	owned: ReadonlyArray<CosmeticItemResponse>,
): Array<CosmeticItem> {
	const ownedIds = new Set(owned.map((item) => item.id));
	const catalogIds = new Set(catalog.map((item) => item.id));
	return [...catalog, ...owned.filter((item) => !catalogIds.has(item.id))]
		.map((item) => ({...item, owned: ownedIds.has(item.id)}))
		.filter((item) => item.owned || item.availability !== 'owned' || isRoomPartType(item.type));
}

export {isCosmeticLocked} from '@app/features/cosmetics/utils/CosmeticInventory';

export function isRoomPartLocked(item: CosmeticItem, isPremium: boolean): boolean {
	return !item.owned && (item.availability === 'owned' || (item.availability === 'premium' && !isPremium));
}

class Cosmetics {
	items: ReadonlyArray<CosmeticItem> = [];
	profileEffectId: string | null = null;
	// null wears the default character; a null shirt derives the colour from the user ID.
	roomCharacterId: string | null = null;
	roomCharacterShirt: number | null = null;
	roomOutfit: RoomOutfit = EMPTY_ROOM_OUTFIT;
	private generation = 0;
	ready = false;
	failed = false;

	constructor() {
		makeAutoObservable<Cosmetics, 'generation'>(this, {generation: false}, {autoBind: true});
	}

	itemsOfType(type: CosmeticType): Array<CosmeticItem> {
		return this.items.filter((item) => item.type === type);
	}

	// Parts only an owner can wear are left out until owned, like every other type.
	get roomParts(): Array<CosmeticItem> {
		return this.items.filter(isWearableRoomPart);
	}

	modelKeyOf(itemId: string): string | null {
		return this.getItem(itemId)?.model_key ?? null;
	}

	getItem(itemId: string | null): CosmeticItem | null {
		return itemId ? (this.items.find((item) => item.id === itemId) ?? null) : null;
	}

	// The frame, ultra decoration and nameplate are read off the current user, which USER_UPDATE keeps current in every session.
	equippedId(type: WearableType): string | null {
		const user = Users.currentUser;
		switch (type) {
			case 'avatar_decoration':
				return user?.avatarDecoration?.item_id ?? null;
			case 'avatar_ultra':
				return user?.avatarUltra?.item_id ?? null;
			case 'nameplate':
				return user?.nameplate?.item_id ?? null;
			case 'profile_effect':
				return this.profileEffectId;
			case ROOM_CHARACTER_TYPE:
				return this.roomCharacterId;
			default:
				return this.roomOutfit[roomSlotOf(type)];
		}
	}

	reset(): void {
		this.generation++;
		this.items = [];
		this.profileEffectId = null;
		this.roomCharacterId = null;
		this.roomCharacterShirt = null;
		this.roomOutfit = EMPTY_ROOM_OUTFIT;
		this.ready = false;
		this.failed = false;
	}

	async load(): Promise<void> {
		if (AccountScopedWork.isSuspended) return;
		const generation = this.generation;
		this.failed = false;
		try {
			const [catalog, mine] = await Promise.all([
				CosmeticCommands.fetchCatalog(),
				CosmeticCommands.fetchUserCosmetics(),
			]);
			runInAction(() => {
				if (generation !== this.generation) return;
				this.items = mergeCosmeticItems(catalog, mine.owned);
				this.profileEffectId = mine.equipped.profile_effect_id;
				this.roomCharacterId = mine.equipped.room_character_id ?? null;
				this.roomCharacterShirt = mine.equipped.room_character_shirt ?? null;
				this.roomOutfit = readOutfit(mine.equipped);
				this.ready = true;
			});
		} catch {
			runInAction(() => {
				if (generation !== this.generation) return;
				this.failed = true;
			});
		}
	}

	// Sends only the changed slots: every save fans the user change out to all of their communities.
	async equip(selection: CosmeticSelection): Promise<void> {
		if (AccountScopedWork.isSuspended) return;
		const {[ROOM_CHARACTER_TYPE]: roomCharacter, ...rest} = selection;
		if (roomCharacter !== undefined) await this.equipRoomCharacter(roomCharacter, this.roomCharacterShirt);
		let outfit: RoomOutfit | null = null;
		const body: CosmeticCommands.EquipCosmeticsBody = {};
		for (const [type, itemId] of Object.entries(rest) as Array<
			[Exclude<WearableType, 'room_character'>, string | null]
		>) {
			if (isRoomPartType(type))
				outfit = wearRoomPart(outfit ?? this.roomOutfit, roomSlotOf(type), itemId, this.modelKeyOf);
			else body[EQUIP_FIELDS[type]] = itemId;
		}
		if (outfit) await this.equipRoomOutfit(changedSlots(outfit, this.roomOutfit));
		if (Object.keys(body).length === 0) return;
		const generation = this.generation;
		const equipped = await CosmeticCommands.equip(body);
		if (generation !== this.generation) return;
		if (selection.profile_effect !== undefined) {
			// The effect lives on the profile, which no gateway event updates.
			runInAction(() => {
				if (generation !== this.generation) return;
				this.profileEffectId = equipped.profile_effect_id;
			});
			UserProfileCommands.clearCurrentUserProfiles();
		}
	}

	// No gateway event carries the character, so the response is the worn value. A room already showing me gets it
	// through my own connection's LiveKit attribute, which only the server writes.
	async equipRoomCharacter(itemId: string | null, shirt: number | null): Promise<void> {
		if (AccountScopedWork.isSuspended) return;
		const generation = this.generation;
		const worn = await CosmeticCommands.equipRoomCharacter(itemId, shirt);
		if (generation !== this.generation) return;
		runInAction(() => {
			this.roomCharacterId = worn.room_character_id;
			this.roomCharacterShirt = worn.room_character_shirt;
		});
		await this.refreshWornRoomCharacter();
	}

	// Like the whole character, no gateway event carries the outfit: the response is what is worn. shirt undefined
	// leaves the colour as it is.
	async equipRoomOutfit(changes: Partial<RoomOutfit>, shirt?: number | null): Promise<void> {
		if (AccountScopedWork.isSuspended) return;
		const generation = this.generation;
		const body: CosmeticCommands.RoomOutfitBody = {};
		for (const [slot, itemId] of Object.entries(changes) as Array<[RoomOutfitSlot, string | null]>) {
			body[`${slot}_id`] = itemId;
		}
		if (shirt !== undefined) body.shirt = shirt;
		const worn = await CosmeticCommands.equipRoomOutfit(body);
		if (generation !== this.generation) return;
		runInAction(() => {
			this.roomOutfit = readOutfit(worn);
			this.roomCharacterId = worn.room_character_id;
			this.roomCharacterShirt = worn.room_character_shirt;
		});
		await this.refreshWornRoomCharacter();
	}

	// Wearing parts takes the whole character off on the server; with no part changed the character endpoint does it.
	async saveRoomLook({characterId, outfit, shirt}: RoomLook): Promise<void> {
		if (characterId !== null) return this.equipRoomCharacter(characterId, shirt);
		const changes = changedSlots(outfit, this.roomOutfit);
		if (Object.keys(changes).length === 0) return this.equipRoomCharacter(null, shirt);
		return this.equipRoomOutfit(changes, shirt === this.roomCharacterShirt ? undefined : shirt);
	}

	// After the worn character changed on the server: the voice room I am in shows it only if that channel has a room
	// theme. A flat channel or a call has no characters to update.
	async refreshWornRoomCharacter(): Promise<void> {
		// Loaded here so the profile and shop surfaces that read this store do not pull in the voice engine.
		const [
			{default: MediaEngine},
			{default: ExperimentAssignments},
			{default: Channels},
			{effectiveRoomTheme},
			{refreshRoomCharacter},
		] = await Promise.all([
			import('@app/features/voice/engine/MediaEngineFacade'),
			import('@app/features/experiment/state/ExperimentAssignments'),
			import('@app/features/channel/state/Channels'),
			import('@app/features/voice_scene/utils/EffectiveRoomTheme'),
			import('@app/features/voice_scene/commands/VoiceSeatCommands'),
		]);
		const channelId = MediaEngine.channelId;
		const channel = channelId ? Channels.getChannel(channelId) : undefined;
		const assignment = readVoiceRoomAssignment(ExperimentAssignments.response);
		if (
			channelId &&
			channel &&
			assignment.avatars &&
			effectiveRoomTheme(channel.roomTheme, assignment.default_room_theme, channel.type)
		) {
			await refreshRoomCharacter(channelId);
		}
	}
}

const cosmetics = new Cosmetics();
ResettableStates.register(cosmetics, cosmetics.reset);
AccountScopedWork.registerCancellation(() => cosmetics.reset());

export default cosmetics;
